import { useState } from 'react';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';

import { faCommentSms } from '@fortawesome/pro-solid-svg-icons/faCommentSms';
import { faCommentHeart } from '@fortawesome/pro-regular-svg-icons/faCommentHeart';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons/faEnvelope';
import { faLink } from '@fortawesome/pro-solid-svg-icons/faLink';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Alert } from 'us-react-components/dist/Alert/Alert';
import { Button, ButtonSize, ButtonType } from '../components';
import { useDependencies } from '../dependencies';
import { useModal } from './modalContext';

const iconClasses = 'text-sapphire h-[32px] w-[32px] text-center rounded-full mx-2';

export const GetRecommendationsModal = (): JSX.Element => {
  const { services, stores } = useDependencies();
  const { sitter: { sitter = {} } } = stores;
  const { config } = services;
  const modal = useModal();
  const { t } = useSharedTranslation(namespaces.caregiver);

  const [showAlert, setShowAlert] = useState<boolean>(false);

  const getShareUrl = (): string => `${config.SERVER_URL}/sitter/${sitter.id}/recommend`;

  const closeModal = (): void => {
    modal?.setModalName(undefined);
  }

  const copyUrl = (): void => {
    if (navigator.clipboard) {
      try {
        navigator.clipboard.writeText(getShareUrl());
        setShowAlert(true);
      } catch (e) {
        console.warn(e);
      }
    }
  };

  const openEmail = (): void => {
    window.open(`mailto:?subject=${t('components.getRecommendations.emailSubject',
      { firstName: sitter?.firstName || '' })}&body=${t('components.getRecommendations.emailBody',
      { url: getShareUrl(), firstName: sitter?.firstName || '' })}`);
  };

  const selectAll = (el: HTMLInputElement): void => {
    el.focus();
    el.select();
    copyUrl();
  };

  const shareUrl = async (): Promise<void> => {
    if (navigator.share) {
      try {
        await navigator.share({ url: getShareUrl() });
      } catch (e) {
        console.warn(e);
      }
    }
  };

  return (
    <div>
      <div className='text-xl md:text-2xl'>{t('components.getRecommendations.heading')}</div>
      <FontAwesomeIcon icon={faXmark} className='text-neutral-500 hover:text-neutral-700 absolute top-4 right-4 cursor-pointer' onClick={closeModal} />
      <div className='rounded-2xl border-neutral-100 border-2 p-4 my-2'>
        <div className='flex mb-3 text-16'>
          <FontAwesomeIcon className='mr-3 text-bubblegum text-xl' icon={faCommentHeart} />
          <div className='font-bold'>{t('components.getRecommendations.title')}</div>
        </div>
        <div className='mb-3'>{t('components.getRecommendations.body')}</div>
        <input type='text' className='font-semibold mb-3 w-full' onClick={(el): void => selectAll(el.currentTarget)} value={getShareUrl()} />
        <Alert className='!mb-2' color='success' isOpen={showAlert}>
          {t('common.linkCopied')}
        </Alert>
        <div className='flex flex-col place-items-center'>
          <div className='flex items-center p-1'>
            <Button
              buttonSize={ButtonSize.small}
              buttonType={ButtonType.rounded}
              className={iconClasses}
              onClick={(): void => openEmail()}
            >
              <FontAwesomeIcon
                icon={faEnvelope}
                title={`${t('common.email')} ${t('common.link')}`}
              />
            </Button>
            { (!!navigator.share) && (
              <Button
                buttonSize={ButtonSize.small}
                buttonType={ButtonType.rounded}
                className={iconClasses}
                onClick={(): void => { void shareUrl(); }}
              >
                <FontAwesomeIcon
                  icon={faCommentSms}
                  title={`${t('common.share')} ${t('common.link')}`}
                />
              </Button>
            )}
            <Button
              buttonSize={ButtonSize.small}
              buttonType={ButtonType.rounded}
              className={iconClasses}
              onClick={(): void => copyUrl()}
            >
              <FontAwesomeIcon
                icon={faLink}
                title={`${t('common.copy')} ${t('common.link')}`}
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
